import React, { useCallback, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import U2000SaveCancelDialog from '../../../u2000Core/components/dialog/U2000SaveCancelDialog';
import U2000ComboBoxBase from '../../../u2000Core/components/formControls/combobox/U2000ComboBoxBase';
import U2000InputText from '../../../u2000Core/components/formControls/input/U2000InputText';
import U2000InputMemo from '../../../u2000Core/components/formControls/input/U2000InputMemo';
import U2000InputDate from '../../../u2000Core/components/formControls/input/U2000InputDate';
import U2000Label from '../../../u2000Core/components/formControls/label/U2000Label';
import u2000FormState, {
  U2000FormStateEnumEditMode,
  U2000FormStateEnumState,
  type U2000FormState,
} from '../../../u2000Core/reducer/formReducer/U2000FormState';
import { u2000IntFormState, useU2000FormStateHandlers } from '../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import { NS } from '../../U3301';
import type { U3301EventCreatePayload, U3301InitLUCategory } from '../../U3301Service';
import { u3301ValidateInclusiveDateInterval } from '../../utils/u3301DateInterval';

type CategoryOption = { id: string; label: string };

type CreateFormRow = {
  categoryId: string;
  title: string;
  description: string;
  eventDate: string;
  startDate: string;
  endDate: string;
};

interface U3301CreateManualEventDialogProps {
  initialDate: string;
  initialEndDate?: string;
  categories: U3301InitLUCategory[];
  onClose: () => void;
  onSave: (payload: U3301EventCreatePayload) => Promise<void>;
}

const U3301CreateManualEventDialog: React.FC<U3301CreateManualEventDialogProps> = ({
  initialDate,
  initialEndDate,
  categories,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);

  const categoryOptions: CategoryOption[] = useMemo(
    () => categories.map((c) => ({ id: c.id, label: c.label })),
    [categories],
  );

  const [formState, formStateDispatch] = useReducer(
    u2000FormState,
    {
      initialDate,
      initialEndDate: initialEndDate ?? initialDate,
      firstCategoryId: categories[0]?.id ?? '',
    },
    ({ initialDate: date, initialEndDate: end, firstCategoryId }): U2000FormState<CreateFormRow> => ({
      ...u2000IntFormState,
      state: U2000FormStateEnumState.EDIT,
      editMode: U2000FormStateEnumEditMode.EDIT,
      current: {
        categoryId: firstCategoryId,
        title: '',
        description: '',
        eventDate: date,
        startDate: date,
        endDate: end,
      },
      old: {
        categoryId: firstCategoryId,
        title: '',
        description: '',
        eventDate: date,
        startDate: date,
        endDate: end,
      },
    }),
  );

  const formStateHandlers = useU2000FormStateHandlers<CreateFormRow>({
    formState,
    formStateDispatch,
  });

  const row = formStateHandlers.getData();
  const selectedCategoryMeta = categories.find((c) => c.id === row.categoryId);
  const dateMode = selectedCategoryMeta?.dateMode ?? 'SINGLE';
  const isInterval = dateMode === 'INTERVAL';

  const selectedCategory = categoryOptions.find((c) => c.id === row.categoryId) ?? null;

  const intervalValidation = isInterval
    ? u3301ValidateInclusiveDateInterval(row.startDate, row.endDate)
    : { ok: true as const, dayCount: 1 };

  const isValid =
    row.categoryId !== '' &&
    row.title.trim() !== '' &&
    (isInterval
      ? intervalValidation.ok && row.startDate.trim() !== '' && row.endDate.trim() !== ''
      : row.eventDate.trim() !== '');

  const intervalErrorKey =
    !intervalValidation.ok && intervalValidation.error === 'order'
      ? 'errIntervalOrder'
      : !intervalValidation.ok && intervalValidation.error === 'tooLong'
        ? 'errIntervalTooLong'
        : !intervalValidation.ok
          ? 'errIntervalInvalid'
          : null;

  const handleEventDateChange = useCallback((eventDate: string) => {
    const current = formStateHandlers.getData();
    if (current.eventDate === eventDate) return;
    formStateHandlers.setData({ ...current, eventDate });
  }, [formStateHandlers]);

  const handleStartDateChange = useCallback((startDate: string) => {
    const current = formStateHandlers.getData();
    const endDate = current.endDate < startDate ? startDate : current.endDate;
    if (current.startDate === startDate && current.endDate === endDate) return;
    formStateHandlers.setData({ ...current, startDate, endDate });
  }, [formStateHandlers]);

  const handleEndDateChange = useCallback((endDate: string) => {
    const current = formStateHandlers.getData();
    if (current.endDate === endDate) return;
    formStateHandlers.setData({ ...current, endDate });
  }, [formStateHandlers]);

  const handleClose = async (action: 'save' | 'cancel') => {
    if (action === 'cancel') {
      onClose();
      return;
    }
    if (!isValid || saving) return;
    setSaving(true);
    try {
      if (isInterval) {
        await onSave({
          categoryId: row.categoryId,
          title: row.title,
          description: row.description,
          startDate: row.startDate,
          endDate: row.endDate,
        });
      } else {
        await onSave({
          categoryId: row.categoryId,
          title: row.title,
          description: row.description,
          eventDate: row.eventDate,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <U2000SaveCancelDialog
      isOpen
      onClose={handleClose}
      title={t('btnCreateEvent', { ns: NS })}
      isValid={isValid && !saving && categories.length > 0}
      width="medium"
      contentOverflowVisible
    >
      <div className="space-y-3 p-1">
        {categories.length === 0 ? (
          <p className="text-sm text-amber-700">{t('msgNoManualCategory', { ns: NS })}</p>
        ) : (
          <>
            <div>
              <U2000Label label={t('lblCategory', { ns: NS })} required />
              <U2000ComboBoxBase<CategoryOption>
                keyField="id"
                descField="label"
                data={categoryOptions}
                value={selectedCategory}
                onChange={(key) => {
                  const nextId = key ?? '';
                  const current = formStateHandlers.getData();
                  const nextMeta = categories.find((c) => c.id === nextId);
                  const nextInterval = nextMeta?.dateMode === 'INTERVAL';
                  formStateHandlers.setData({
                    ...current,
                    categoryId: nextId,
                    ...(nextInterval
                      ? {
                          startDate: current.startDate || current.eventDate,
                          endDate: current.endDate || current.startDate || current.eventDate,
                        }
                      : {
                          eventDate: current.eventDate || current.startDate,
                        }),
                  });
                }}
                placeHolder={t('lblCategory', { ns: NS })}
                noneItemLabel={t('lblCategory', { ns: NS })}
              />
            </div>

            <U2000InputText
              formStateHandlers={formStateHandlers}
              fieldName="title"
              label={t('lblEventTitle', { ns: NS })}
              required
            />

            <U2000InputMemo
              formStateHandlers={formStateHandlers}
              fieldName="description"
              label={t('lblEventDescription', { ns: NS })}
              rows={3}
            />

            {isInterval ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <U2000Label label={t('lblStartDate', { ns: NS })} required />
                  <U2000InputDate value={row.startDate} onChange={handleStartDateChange} />
                </div>
                <div>
                  <U2000Label label={t('lblEndDate', { ns: NS })} required />
                  <U2000InputDate value={row.endDate} onChange={handleEndDateChange} />
                </div>
                {intervalErrorKey && (
                  <p className="text-sm text-red-600 sm:col-span-2">{t(intervalErrorKey, { ns: NS })}</p>
                )}
              </div>
            ) : (
              <div>
                <U2000Label label={t('lblEventDate', { ns: NS })} required />
                <U2000InputDate value={row.eventDate} onChange={handleEventDateChange} />
              </div>
            )}
          </>
        )}
      </div>
    </U2000SaveCancelDialog>
  );
};

export default U3301CreateManualEventDialog;
